/** @jsxImportSource react */
'use client';

import { useEffect } from 'react';

import { usePathname } from 'next/navigation';

import { readAttributionParams } from '@onark/shared/attribution';

/**
 * Records first-touch ad attribution on the MARKETING app.
 *
 * Why this exists: onark.app is a multi-zone setup — the frontend app owns the
 * domain and this marketing app is proxied in under the SAME origin. The
 * frontend records first-touch (utils/attribution.ts) only on its own pages, so
 * an ad click that first lands on a marketing page (/welcome-aboard, /pricing,
 * /planning-center, …) never got its utm/gclid/fbclid recorded — the signup form
 * then read an empty cookie and Mission Control logged the lead as "Direct".
 * PostHog measured ~64% of signups as ad-sourced (half from Meta) that MC was
 * losing this way.
 *
 * This writes the exact same `onark-attribution` cookie the frontend's
 * getFirstTouch() reads. Because it's the same origin, a first touch recorded
 * here is read by the signup form on the frontend at capture time.
 *
 * ⚠️ Keep in sync with apps/frontend/utils/attribution.ts: the cookie NAME,
 * FIELD names, MAX-AGE, and ENCODING must match. The reader uses universal-cookie
 * (`decodeURIComponent` + `JSON.parse`), so we write
 * `encodeURIComponent(JSON.stringify(obj))`. First-touch wins: once the cookie
 * exists it is never overwritten, so later navigation can't clobber the origin.
 */

const COOKIE = 'onark-attribution';
// 90 days — matches the frontend util.
const MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

function cookieExists(name: string): boolean {
  return document.cookie.split('; ').some(c => c.startsWith(`${name}=`));
}

export function FirstTouchAttribution() {
  // Re-run on client navigation so the very first landing (which may be a later
  // route in the same session) still gets recorded. Deliberately NOT
  // useSearchParams — that would opt every page out of static rendering; we read
  // the query off window.location inside the effect instead.
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (cookieExists(COOKIE)) return; // first touch already recorded

    // Only an off-site referrer is meaningful as origin; same-origin nav isn't.
    const referrer =
      document.referrer && !document.referrer.startsWith(window.location.origin)
        ? document.referrer
        : undefined;

    // Query-param fields via the shared parser (a new ad param is added in one
    // place, and the malformed-link guard is common to both capture sites —
    // see docs/attribution-ad-url-convention.md). referrer + landingPath come
    // from the DOM, not the query string.
    const touch: Record<string, string> = {
      ...readAttributionParams(window.location.search),
    };
    const put = (k: string, v: string | undefined) => {
      if (v) touch[k] = v;
    };

    put('referrer', referrer);
    put('landingPath', window.location.pathname || undefined);

    // Don't lock in a meaningless "direct" first touch (no utm, click id, or
    // off-site referrer) — that would block a later, genuinely-attributed
    // landing in the same session from being recorded.
    const hasSignal =
      touch.utmSource ||
      touch.utmMedium ||
      touch.utmCampaign ||
      touch.gclid ||
      touch.fbclid ||
      touch.referrer ||
      touch.adId;

    if (!hasSignal) return;

    const secure = window.location.protocol === 'https:';

    document.cookie = `${COOKIE}=${encodeURIComponent(
      JSON.stringify(touch)
    )}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${
      secure ? '; Secure' : ''
    }`;
  }, [pathname]);

  return null;
}
